// src/hooks/useOrderManager.js - Complete Multi-Order Management Hook

import { useState, useEffect, useCallback } from 'react';
import {
  getActiveOrders,
  getCurrentOrder,
  getCurrentOrderId,
  createAndSetNewOrder,
  deleteOrder,
  duplicateOrder,
  saveOrder,
  setCurrentOrderId,
  initializeMultiOrderSystem,
  getOrderBroadcastChannel
} from '../../utils/statePersistence';

export const useOrderManager = () => {
  const [activeOrders, setActiveOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load initial data 🚀 Initializing order manager...
  useEffect(() => {
    initializeMultiOrderSystem();
    refreshOrders();
  }, []);

  // Refresh orders from localStorage
  const refreshOrders = useCallback(() => {
    try {
      setIsLoading(true);
      const orders = getActiveOrders();
      const current = getCurrentOrder();
      
      setActiveOrders(orders);
      setCurrentOrder(current);
                                                       
      // If we have orders but no current order, set the first one as current
      if (orders.length > 0 && !current) {
        console.log('🔄 No current order but orders exist, setting first as current...');
        setCurrentOrderId(orders[0].id);
        setCurrentOrder(orders[0]);
      }
      
    } catch (error) {
      console.error('❌ Failed to refresh orders:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Create new order
  const createNewOrder = useCallback((orderType = '', customerName = '') => {
    try {
      console.log(`🆕 Creating new order: ${orderType} - ${customerName}`);
      const newOrder = createAndSetNewOrder(orderType, customerName);
      refreshOrders();
      console.log(`✅ Created new order: ${newOrder.id}`);
      return newOrder;
    } catch (error) {
      console.error('❌ Failed to create new order:', error);
      return null;
    }
  }, [refreshOrders]);

  // Switch to existing order
  const switchToOrder = useCallback((orderId) => {
    try {
      console.log(`🔄 Switching to order: ${orderId}`);
      setCurrentOrderId(orderId);
      refreshOrders();
      return true;
    } catch (error) {
      console.error(`❌ Failed to switch to order ${orderId}:`, error);
      return false;
    }
  }, [refreshOrders]);

  // Close/delete order
  // Options: { force: boolean, isSubmission: boolean }
  const closeOrder = useCallback((orderId, forceOrOptions = false) => {
      try {
          // Support both old signature (orderId, force) and new signature (orderId, options)
          const options = typeof forceOrOptions === 'boolean'
            ? { force: forceOrOptions, isSubmission: false }
            : { force: false, isSubmission: false, ...forceOrOptions };

          const orderToClose = activeOrders.find(order => order.id === orderId);

          if (!options.force && orderToClose && orderToClose.status === 'draft') {
              // Show confirmation for unsaved changes
              const customerName = orderToClose.formData?.customerName || 'Unnamed Customer';
              const orderNo = orderToClose.formData?.orderNo || 'New Order';
              const confirmed = window.confirm(
                  `Order "${customerName} - ${orderNo}" has unsaved changes. Are you sure you want to close it?`
              );
              if (!confirmed) return false;
          }

          console.log(`🗑️ Closing order: ${orderId}${options.isSubmission ? ' (submitted)' : ''}`);
          deleteOrder(orderId, { isSubmission: options.isSubmission });
          refreshOrders();
          return true;
      } catch (error) {
          console.error(`❌ Failed to close order ${orderId}:`, error);
          return false;
      }
  }, [activeOrders, refreshOrders]);

  // Duplicate order
  const duplicateOrderById = useCallback((sourceOrderId, newCustomerName = '') => {
    try {
      console.log(`📋 Duplicating order: ${sourceOrderId}`);
      const newOrder = duplicateOrder(sourceOrderId, newCustomerName);
      if (newOrder) {
        setCurrentOrderId(newOrder.id);
        refreshOrders();
        console.log(`✅ Duplicated order: ${sourceOrderId} → ${newOrder.id}`);
        return newOrder;
      }
      return null;
    } catch (error) {
      console.error(`❌ Failed to duplicate order ${sourceOrderId}:`, error);
      return null;
    }
  }, [refreshOrders]);

  // Update current order
  const updateCurrentOrder = useCallback((updates) => {
    try {
      if (!currentOrder) {
        console.warn('⚠️ No current order to update');
        return false;
      }

      // 🔧 FIX: Deep merge paymentData to preserve nested agreementState
      const updatedOrder = {
        ...currentOrder,
        ...updates,
        // Deep merge paymentData to prevent agreementState from being wiped out
        paymentData: updates.paymentData ? {
          ...currentOrder.paymentData,
          ...updates.paymentData,
          // Explicitly preserve agreementState if not provided in updates
          agreementState: updates.paymentData.agreementState || currentOrder.paymentData?.agreementState
        } : currentOrder.paymentData,
        lastUpdated: Date.now(),
        status: 'draft' // Mark as draft when changes are made
      };

      // console.log(`💾 Updating order ${updatedOrder.id}:`, Object.keys(updates));
      saveOrder(updatedOrder);
      setCurrentOrder(updatedOrder);

      // Update in activeOrders array
      setActiveOrders(prev =>
        prev.map(order =>
          order.id === updatedOrder.id ? updatedOrder : order
        )
      );

      return true;
    } catch (error) {
      console.error('❌ Failed to update current order:', error);
      return false;
    }
  }, [currentOrder]);

  // Get order display info for tabs
  const getOrderDisplayInfo = useCallback((order) => {
    const customerName = order.formData?.customerName?.trim() || '';
    const orderNo = order.formData?.orderNo || 'New Order';
    const orderType = order.formData?.orderType || '';
    
    // Create title: prefer customer name, fallback to order number
    const title = customerName || orderNo;
    
    // Create subtitle: show order number and type if we have customer name, otherwise just type
    let subtitle = '';
    if (customerName && orderType) {
      subtitle = `${orderType}`;
    } else if (customerName) {
      subtitle = orderNo;
    } else if (orderType) {
      subtitle = orderType;
    } else {
      subtitle = 'New Order';
    }
    
    return {
      title: title,
      subtitle: subtitle,
      hasUnsavedChanges: order.status === 'draft',
      isComplete: order.status === 'completed',
      currentSlide: order.currentSlide || 1
    };
  }, []);

  // Check if order has unsaved changes
  const hasUnsavedChanges = useCallback((orderId) => {
    const order = activeOrders.find(o => o.id === orderId);
    return order?.status === 'draft';
  }, [activeOrders]);

  // Mark order as saved
  const markOrderAsSaved = useCallback((orderId = null) => {
    const targetOrderId = orderId || currentOrder?.id;
    if (!targetOrderId) return false;
    
    const order = activeOrders.find(o => o.id === targetOrderId);
    if (order && order.status === 'draft') {
      const updatedOrder = { ...order, status: 'saved' };
      saveOrder(updatedOrder);
      
      if (targetOrderId === currentOrder?.id) {
        setCurrentOrder(updatedOrder);
      }
      
      setActiveOrders(prev => 
        prev.map(o => o.id === targetOrderId ? updatedOrder : o)
      );
      
      console.log(`✅ Marked order ${targetOrderId} as saved`);
      return true;
    }
    return false;
  }, [activeOrders, currentOrder]);

  // Mark order as completed
  const markOrderAsCompleted = useCallback((orderId = null) => {
    const targetOrderId = orderId || currentOrder?.id;
    if (!targetOrderId) return false;
    
    const order = activeOrders.find(o => o.id === targetOrderId);
    if (order) {
      const updatedOrder = { ...order, status: 'completed' };
      saveOrder(updatedOrder);
      
      if (targetOrderId === currentOrder?.id) {
        setCurrentOrder(updatedOrder);
      }
      
      setActiveOrders(prev => 
        prev.map(o => o.id === targetOrderId ? updatedOrder : o)
      );
      
      console.log(`🎉 Marked order ${targetOrderId} as completed`);
      return true;
    }
    return false;
  }, [activeOrders, currentOrder]);

  // Auto-save functionality
  const autoSave = useCallback(() => {
    if (currentOrder && currentOrder.status === 'draft') {
      saveOrder(currentOrder);
      console.log(`💾 Auto-saved order: ${currentOrder.id}`);
    }
  }, [currentOrder]);

  // Set up auto-save interval
  useEffect(() => {
    const interval = setInterval(autoSave, 30000); // Auto-save every 30 seconds
    return () => clearInterval(interval);
  }, [autoSave]);

  // 🔧 FIX: Listen for localStorage changes from other tabs/windows
  // This ensures that when an order is submitted in one tab, other tabs
  // automatically update their order lists and close the submitted order tab
  useEffect(() => {
    let refreshTimeout = null;

    const handleStorageChange = (e) => {
      // Only respond to changes in our WOE order storage keys
      // Note: The actual key is 'woe_active_order_list' (defined in statePersistence.js)
      if (e.key === 'woe_active_order_list' || e.key === 'woe_current_order_id') {
        console.log('📢 Order change detected from another tab/window');

        // 🔧 SMART REFRESH: Check for significant changes that require refresh
        try {
          let shouldRefresh = false;
          let refreshReason = '';

          if (e.key === 'woe_active_order_list') {
            // woe_active_order_list stores an array of order IDs (strings), not full order objects
            const newOrderIds = JSON.parse(e.newValue || '[]');
            const oldOrderIds = JSON.parse(e.oldValue || '[]');

            // Check if order count changed (order added/removed)
            if (newOrderIds.length !== oldOrderIds.length) {
              shouldRefresh = true;
              refreshReason = `Order count changed: ${oldOrderIds.length} → ${newOrderIds.length}`;
            } else {
              // Count is same, but check if any order IDs are different (order replaced)
              const oldIds = new Set(oldOrderIds);
              const newIds = new Set(newOrderIds);

              const hasNewOrders = newOrderIds.some(id => !oldIds.has(id));
              const hasRemovedOrders = oldOrderIds.some(id => !newIds.has(id));

              if (hasNewOrders || hasRemovedOrders) {
                shouldRefresh = true;
                refreshReason = 'Order IDs changed (order closed/created)';
              }
            }
          } else if (e.key === 'woe_current_order_id') {
            // Current order ID changed in another tab
            shouldRefresh = true;
            refreshReason = `Current order ID changed: ${e.oldValue} → ${e.newValue}`;
          }

          if (shouldRefresh) {
            console.log(`📊 ${refreshReason}`);

            // Clear any pending refresh
            if (refreshTimeout) {
              clearTimeout(refreshTimeout);
            }

            // Wait 2 seconds to ensure all auto-saves complete before refreshing
            refreshTimeout = setTimeout(() => {
              console.log('🔄 Refreshing orders after cross-tab change...');
              refreshOrders();
            }, 2000);
          } else {
            console.log('💾 Order data updated but no significant changes - skipping refresh');
          }
        } catch (error) {
          console.error('❌ Error parsing storage change:', error);
        }
      }
    };

    // Add storage event listener (only fires for changes from OTHER tabs)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, [refreshOrders]);

  // 🔧 BroadcastChannel listener for IMMEDIATE cross-tab updates
  // This fires instantly when an order is submitted/deleted in another tab
  useEffect(() => {
    const channel = getOrderBroadcastChannel();
    if (!channel) return;

    const handleBroadcast = (event) => {
      const { type, orderId, isSubmission } = event.data;
      console.log(`📡 Received broadcast: ${type}`, event.data);

      if (type === 'ORDER_DELETED') {
        // Immediately refresh orders when an order is deleted/submitted in another tab
        console.log(`🔄 Order ${orderId} was ${isSubmission ? 'submitted' : 'deleted'} in another tab - refreshing immediately`);
        refreshOrders();
      }
    };

    channel.addEventListener('message', handleBroadcast);

    return () => {
      channel.removeEventListener('message', handleBroadcast);
    };
  }, [refreshOrders]);

  // Get order by ID (utility function)
  const getOrderById = useCallback((orderId) => {
    return activeOrders.find(order => order.id === orderId) || null;
  }, [activeOrders]);

  // Get orders summary
  const getOrdersSummary = useCallback(() => {
    const draftCount = activeOrders.filter(o => o.status === 'draft').length;
    const savedCount = activeOrders.filter(o => o.status === 'saved').length;
    const completedCount = activeOrders.filter(o => o.status === 'completed').length;
    
    return {
      total: activeOrders.length,
      draft: draftCount,
      saved: savedCount,
      completed: completedCount,
      hasUnsavedChanges: draftCount > 0
    };
  }, [activeOrders]);

  return {
    // State
    activeOrders,
    currentOrder,
    isLoading,
    
    // Actions
    createNewOrder,
    switchToOrder,
    closeOrder,
    duplicateOrderById,
    updateCurrentOrder,
    refreshOrders,
    markOrderAsSaved,
    markOrderAsCompleted,
    
    // Utilities
    getOrderDisplayInfo,
    hasUnsavedChanges,
    getOrderById,
    getOrdersSummary,
    
    // Computed values
    hasMultipleOrders: activeOrders.length > 1,
    currentOrderId: currentOrder?.id || null,
    orderCount: activeOrders.length,
    needsAutoSave: currentOrder?.status === 'draft'
  };
};